import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardShell from "../components/DashboardShell";
import Modal from "../components/Modal";
import { api, formatApiError } from "../lib/api";
import { Plus } from "lucide-react";

const STATUS_COLORS = {
  "Open": "#00aaff", "Assigned": "#00aaff", "Work In Progress": "#f59e0b",
  "Pending Customer": "#a855f7", "Resolved": "#39ff14", "Ticket Closed": "#4b5563",
};

export function TicketsList() {
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Phishing / Scam Email", custom_category: "" });
  const [categories, setCategories] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/tickets/mine`, { params: { status: tab } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    api.get(`/tickets/categories`).then((r) => {
      const items = r.data.items || [];
      setCategories(items);
      if (items.length && !items.includes(form.category)) {
        setForm((f) => ({ ...f, category: items[0] }));
      }
    });
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    setErr("");
    try {
      const payload = { title: form.title, description: form.description, category: form.category };
      if (form.category === "Other — Type your issue") {
        payload.custom_category = form.custom_category.trim();
      }
      await api.post(`/tickets`, payload);
      setCreating(false);
      setForm({ title: "", description: "", category: categories[0] || "General Inquiry", custom_category: "" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const groupedCategories = (() => {
    const groups = {
      "Cybersecurity": [],
      "IT / Computer": [],
      "Phone / Mobile": [],
      "General": [],
      "Other": [],
    };
    const otherLabel = "Other — Type your issue";
    const phoneKeywords = ["Phone", "SIM", "Mobile", "Smishing", "Lost or Stolen", "Two-Factor"];
    const itKeywords = ["Computer", "Software", "Operating System", "Email", "Printer", "Wi-Fi", "Password", "File", "Backup", "Browser"];
    for (const c of categories) {
      if (c === otherLabel) groups["Other"].push(c);
      else if (phoneKeywords.some((k) => c.includes(k))) groups["Phone / Mobile"].push(c);
      else if (itKeywords.some((k) => c.includes(k))) groups["IT / Computer"].push(c);
      else if (c.includes("Inquiry") || c.includes("Awareness") || c.includes("Donation")) groups["General"].push(c);
      else groups["Cybersecurity"].push(c);
    }
    return groups;
  })();

  return (
    <DashboardShell>
      <div className="space-y-5" data-testid="tickets-page">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// 05 / Tickets</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Support Tickets</h1>
          </div>
          <button onClick={() => setCreating(true)} className="csc-btn-primary" data-testid="open-create-ticket"><Plus size={13} /> Create Ticket</button>
        </div>

        <Modal open={creating} onClose={() => setCreating(false)} className="p-6 space-y-3" data-testid="create-ticket-modal" ariaLabel="New ticket">
          <h2 className="font-semibold">New Ticket</h2>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="csc-input" data-testid="ticket-title-input" />
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value, custom_category: e.target.value === "Other — Type your issue" ? form.custom_category : "" })}
            className="csc-input"
            data-testid="ticket-category-input"
          >
            {Object.entries(groupedCategories).map(([groupName, list]) =>
              list.length === 0 ? null : (
                <optgroup key={groupName} label={groupName}>
                  {list.map((c) => <option key={c} value={c}>{c}</option>)}
                </optgroup>
              )
            )}
          </select>
          {form.category === "Other — Type your issue" && (
            <input
              value={form.custom_category}
              onChange={(e) => setForm({ ...form, custom_category: e.target.value })}
              placeholder="Describe the issue type (e.g. Smart-TV firmware issue)"
              className="csc-input"
              data-testid="ticket-custom-category-input"
              maxLength={200}
            />
          )}
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={6} placeholder="Describe the issue…" className="csc-input resize-none" data-testid="ticket-description-input" />
          {err && <div className="text-xs text-red-400" data-testid="ticket-create-error">{err}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="csc-btn-ghost">Cancel</button>
            <button
              onClick={submit}
              disabled={form.category === "Other — Type your issue" && !form.custom_category.trim()}
              className="csc-btn-primary"
              data-testid="submit-ticket"
            >
              Create Ticket
            </button>
          </div>
        </Modal>

        <div className="flex items-center gap-2">
          {[["open","Open"],["closed","Resolved / Closed"],["all","All"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`csc-pill ${tab === k ? "csc-pill-active" : ""}`} data-testid={`tab-${k}`}>{label}</button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="csc-card p-8 text-center text-[#94a3b8]" data-testid="tickets-empty">No tickets here.</div>
        ) : (
          <div className="space-y-3">
            {items.map((t) => (
              <Link to={`/tickets/${t.number}`} key={t.id} className="csc-card p-4 flex items-center justify-between gap-3 hover:border-[#00aaff] block" data-testid={`ticket-row-${t.number}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">
                    #{t.number} · {t.custom_category ? `Other — ${t.custom_category}` : t.category}
                  </div>
                  <div className="text-sm text-white truncate mt-1">{t.title}</div>
                  <div className="font-mono-tech text-[10px] text-[#94a3b8] mt-1">Updated {new Date(t.updated_at).toLocaleString()} · Assigned: {t.assigned_to_name || "—"}</div>
                </div>
                <span className="font-mono-tech text-[10px] uppercase tracking-wider px-2 py-1 rounded" style={{ background: `${STATUS_COLORS[t.status] || "#00aaff"}26`, color: STATUS_COLORS[t.status] }}>{t.status}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

export function TicketDetail() {
  const { number } = useParams();
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/tickets/${number}`);
    setT(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [number]);

  const send = async () => {
    if (!reply.trim()) return;
    setErr("");
    try { await api.post(`/tickets/${number}/reply`, { content: reply, is_internal: false }); setReply(""); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  if (!t) return <DashboardShell><div className="csc-card p-8 font-mono-tech text-xs uppercase tracking-wider text-[#94a3b8]">Loading…</div></DashboardShell>;
  return (
    <DashboardShell>
      <div className="space-y-5 max-w-3xl mx-auto" data-testid={`ticket-detail-${t.number}`}>
        <div>
          <Link to="/tickets" className="text-xs font-mono-tech uppercase tracking-wider text-[#94a3b8] hover:text-[#00aaff]">← My tickets</Link>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8] mt-3">
            #{t.number} · {t.custom_category ? `Other — ${t.custom_category}` : t.category}
          </div>
          <h1 className="text-2xl font-semibold mt-1">{t.title}</h1>
          <span className="inline-block mt-2 font-mono-tech text-[10px] uppercase tracking-wider px-2 py-1 rounded" style={{ background: `${STATUS_COLORS[t.status] || "#00aaff"}26`, color: STATUS_COLORS[t.status] }}>{t.status}</span>
        </div>

        <section className="csc-card p-5">
          <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-2">Description</h3>
          <p className="text-sm whitespace-pre-wrap">{t.description}</p>
        </section>

        <section className="csc-card p-5" data-testid="ticket-history">
          <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-2">Status History</h3>
          <ol className="space-y-2">
            {t.history.map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-xs">
                <span className="font-mono-tech text-[10px] uppercase tracking-wider text-[#39ff14]">{h.status}</span>
                <span className="text-[#94a3b8] flex-1">{h.note} <span className="text-[#94a3b8]">· by {h.by_name} · {new Date(h.at).toLocaleString()}</span></span>
              </li>
            ))}
          </ol>
        </section>

        <section className="csc-card p-5" data-testid="ticket-thread">
          <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">Conversation</h3>
          {t.replies.length === 0 ? <div className="text-xs text-[#94a3b8]">No replies yet.</div> : (
            <div className="space-y-3">
              {t.replies.map((r) => (
                <div key={r.id} className={`p-3 rounded ${r.by_role === "admin" ? "bg-[#00aaff]/10 border border-[#00aaff]/30" : "bg-[#0d1b2e] border border-[rgba(0,170,255,0.18)]"}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white">{r.by_name} {r.by_role === "admin" && <span className="font-mono-tech text-[10px] text-[#00aaff] ml-1">SUPPORT</span>}</span>
                    <span className="font-mono-tech text-[10px] text-[#94a3b8]">{new Date(r.at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap mt-2">{r.content}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-2">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Add a reply…" className="csc-input resize-none" data-testid="ticket-reply-input" />
            {err && <div className="text-xs text-red-400">{err}</div>}
            <div className="flex justify-end">
              <button onClick={send} disabled={!reply.trim()} className="csc-btn-primary" data-testid="ticket-reply-submit">Send</button>
            </div>
          </div>
        </section>
      </div>
    </DashboardShell>
  );
}

export default TicketsList;
